import {
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  makeStyles,
  TextField,
  Theme,
  ThemeProvider,
  Typography,
  useTheme,
} from '@material-ui/core'
import { Close, Save } from '@material-ui/icons'
import React, { useEffect, useReducer, useState } from 'react'
import {
  Datagrid,
  DateField,
  Filter,
  FunctionField,
  Labeled,
  Pagination,
  TextField as RATextField,
  ResourceContextProvider,
  sanitizeFetchType,
  SelectInput,
  SimpleShowLayout,
  useMutation,
  useNotify,
  useQuery,
  useRefresh,
  useShowContext,
  useTranslate,
} from 'react-admin'
import useStateRef from 'react-usestateref'
import { ResourceName } from '../../../../core/ResourceName'
import { ConfirmDialog } from '../../../common/ConfirmDialog'
import Button from '../../../common/customized-mui-components/Button'
import DraggableComponent from '../../../common/DraggableComponent'
import { IksorisParkingTicketCashMachineDto } from '../../../../core/dto/device/ticket-machines/iksoris-parking-ticket-cash-machine/iksoris-parking-ticket-cash-machine.dto'
import List from '../../../common/customized-ra-components/List'
import { PaymentType } from '../../../../core/dto/device/ticket-machines/automat-api/transaction-info/enums/payment-type'
import { TransactionStatus } from '../../../../core/dto/device/ticket-machines/automat-api/transaction-info/enums/transaction-status'
import { TransactionType } from '../../../../core/dto/device/ticket-machines/automat-api/transaction-info/enums/transaction-type'
import { extendedLightTheme } from '../../../layout/app.theme'
import { TransactionInfoResponse } from '../../../../core/dto/device/ticket-machines/automat-api/transaction-info-response'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogProperty: {
      marginBottom: theme.spacing(1),
    },
    bulkStateActions: {
      height: '16px',
      background: 'rgba(0,0,0,0.2)',
      minWidth: '300px',
      userSelect: 'none',
    },
    bulkProgressTitle: {
      paddingLeft: '10px',
      width: '100%',
    },
    bulkProgressCloseButton: {
      margin: 0,
    },
    actionsButton: {
      margin: '2%',
      width: '96%',
    },
    textArea: {
      marginTop: '10px',
    },
    dialogContent: {
      padding: '8px',
    },
    crossIcon: {
      position: 'relative',
      left: -24,
      top: 0,
      fontSize: 34,
      opacity: 0.8,
    },
    hide: {
      display: 'none',
    },
  }),
)

export const SetAutomatAPICredentialsDialog = ({
  open,
  close,
  resource,
}: {
  open: boolean
  close: () => void
  resource: ResourceName
}) => {
  const classes = useStyles()
  const [loginValue, setLoginValue] = useState('')
  const [loginError, setLoginError, loginErrorCurrentValue] = useStateRef(false)
  const [loginErrorText, setLoginErrorText] = useState('')
  const [passwordValue, setPasswordValue] = useState('')
  const [passwordError, setPasswordError, passwordErrorCurrentValue] =
    useStateRef(false)
  const [passwordErrorText, setPasswordErrorText] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const { record: ticketMachine } =
    useShowContext<IksorisParkingTicketCashMachineDto>()
  const [mutate] = useMutation()
  const refresh = useRefresh()
  const translate = useTranslate()
  const notify = useNotify()

  const handleClose = () => {
    setLoginValue('')
    setPasswordValue('')
    setLoginError(false)
    setPasswordError(false)
    setLoginErrorText('')
    setPasswordErrorText('')
    close()
  }

  const handleSetAutomatAPICredentials = async () => {
    setConfirmOpen(false)
    setLoginError(false)
    setPasswordError(false)
    setLoginErrorText('')
    setPasswordErrorText('')
    if (loginValue.replace(/\s/g, '').length === 0) {
      setLoginError(true)
      setLoginErrorText(
        translate(
          'resources.iksoris-parking-ticket-cash-machines.dialogs.setAutomatAPICredentials.login.error-bad-string',
        ),
      )
    } else if (loginValue.replace(/\s/g, '').length !== loginValue.length) {
      setLoginError(true)
      setLoginErrorText(
        translate(
          'resources.iksoris-parking-ticket-cash-machines.dialogs.setAutomatAPICredentials.login.error-no-spaces',
        ),
      )
    }
    if (passwordValue.replace(/\s/g, '').length === 0) {
      setPasswordError(true)
      setPasswordErrorText(
        translate(
          'resources.iksoris-parking-ticket-cash-machines.dialogs.setAutomatAPICredentials.password.error-bad-string',
        ),
      )
    } else if (
      passwordValue.replace(/\s/g, '').length !== passwordValue.length
    ) {
      setPasswordError(true)
      setPasswordErrorText(
        translate(
          'resources.iksoris-parking-ticket-cash-machines.dialogs.setAutomatAPICredentials.password.error-no-spaces',
        ),
      )
    }

    if (!loginErrorCurrentValue.current && !passwordErrorCurrentValue.current) {
      await mutate(
        {
          type: sanitizeFetchType('setAutomatAPICredentials'),
          resource,
          payload: {
            ticketMachineId: ticketMachine?.id,
            login: loginValue,
            password: passwordValue,
          },
        },
        {
          returnPromise: true,
          onFailure: (err) => notify(err?.message, 'error'),
        },
      )
      refresh()
      handleClose()
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableBackdropClick
      disableEscapeKeyDown
      PaperComponent={DraggableComponent}
      aria-labelledby="draggable-dialog-title"
    >
      <ConfirmDialog
        open={confirmOpen}
        cancel={() => setConfirmOpen(false)}
        confirm={handleSetAutomatAPICredentials}
        source="setAutomatAPICredentials"
        action="set"
        resource={resource}
      />
      <DialogActions
        className={classes.bulkStateActions}
        style={{ cursor: 'move' }}
      >
        <Typography
          className={classes.bulkProgressTitle}
          id="draggable-dialog-title"
        >
          {translate(
            'resources.iksoris-parking-ticket-cash-machines.dialogs.setAutomatAPICredentials.title',
          )}
        </Typography>
        <IconButton
          size="small"
          className={classes.bulkProgressCloseButton}
          onClick={handleClose}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogActions>
      <DialogContent>
        <TextField
          className={classes.dialogProperty}
          label={translate(
            'resources.iksoris-parking-ticket-cash-machines.dialogs.setAutomatAPICredentials.login.label',
          )}
          variant="outlined"
          value={loginValue}
          onChange={(e) => setLoginValue(e.target.value)}
          fullWidth
          error={loginError}
          helperText={loginErrorText}
        />
        <TextField
          label={translate(
            'resources.iksoris-parking-ticket-cash-machines.dialogs.setAutomatAPICredentials.password.label',
          )}
          variant="outlined"
          value={passwordValue}
          onChange={(e) => setPasswordValue(e.target.value)}
          fullWidth
          type="password"
          autoComplete="new-password"
          error={passwordError}
          helperText={passwordErrorText}
        />
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={() => setConfirmOpen(true)}
          label={translate('ra.action.save')}
          useSmallVersionBreakpoint={false}
          className={classes.actionsButton}
        />
        <Button
          variant="contained"
          onClick={handleClose}
          label={translate('const.cancel')}
          useSmallVersionBreakpoint={false}
          className={classes.actionsButton}
        />
      </DialogActions>
    </Dialog>
  )
}

export const SetTicketMachineAPIKeyDialog = ({
  open,
  close,
  resource,
}: {
  open: boolean
  close: () => void
  resource: ResourceName
}) => {
  const classes = useStyles()
  const [apiKeyValue, setAPIKeyValue] = useState('')
  const [apiKey, setAPIKeyError, apiKeyErrorCurrentValue] = useStateRef(false)
  const [apiKeyErrorText, setAPIKeyErrorText] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const { record: ticketMachine } =
    useShowContext<IksorisParkingTicketCashMachineDto>()
  const [mutate] = useMutation()
  const refresh = useRefresh()
  const translate = useTranslate()
  const notify = useNotify()

  const handleClose = () => {
    setAPIKeyValue('')
    setAPIKeyError(false)
    setAPIKeyErrorText('')
    close()
  }

  const handleSetAutomatAPICredentials = async () => {
    setConfirmOpen(false)
    setAPIKeyError(false)
    setAPIKeyErrorText('')
    if (apiKeyValue.replace(/\s/g, '').length === 0) {
      setAPIKeyError(true)
      setAPIKeyErrorText(
        translate(
          'resources.iksoris-parking-ticket-cash-machines.dialogs.setAPIKey.apiKey.error-bad-string',
        ),
      )
    } else if (apiKeyValue.replace(/\s/g, '').length !== apiKeyValue.length) {
      setAPIKeyError(true)
      setAPIKeyErrorText(
        translate(
          'resources.iksoris-parking-ticket-cash-machines.dialogs.setAPIKey.apiKey.error-no-spaces',
        ),
      )
    }

    if (!apiKeyErrorCurrentValue.current) {
      await mutate(
        {
          type: sanitizeFetchType('setAPIKey'),
          resource,
          payload: {
            ticketMachineId: ticketMachine?.id,
            apiKey: apiKeyValue,
          },
        },
        {
          returnPromise: true,
          onFailure: (err) => notify(err?.message, 'error'),
        },
      )
      refresh()
      handleClose()
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableBackdropClick
      disableEscapeKeyDown
      PaperComponent={DraggableComponent}
      aria-labelledby="draggable-dialog-title"
    >
      <ConfirmDialog
        open={confirmOpen}
        cancel={() => setConfirmOpen(false)}
        confirm={handleSetAutomatAPICredentials}
        source="apiKey"
        action="set"
        resource={resource}
      />
      <DialogActions
        className={classes.bulkStateActions}
        style={{ cursor: 'move' }}
      >
        <Typography
          className={classes.bulkProgressTitle}
          id="draggable-dialog-title"
        >
          {translate(
            'resources.iksoris-parking-ticket-cash-machines.dialogs.setAPIKey.title',
          )}
        </Typography>
        <IconButton
          size="small"
          className={classes.bulkProgressCloseButton}
          onClick={handleClose}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogActions>
      <DialogContent>
        <TextField
          label={translate(
            'resources.iksoris-parking-ticket-cash-machines.dialogs.setAPIKey.apiKey.label',
          )}
          variant="outlined"
          value={apiKeyValue}
          onChange={(e) => setAPIKeyValue(e.target.value)}
          fullWidth
          type="password"
          error={apiKey}
          helperText={apiKeyErrorText}
        />
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={() => setConfirmOpen(true)}
          label={translate('ra.action.save')}
          useSmallVersionBreakpoint={false}
          className={classes.actionsButton}
        />
        <Button
          variant="contained"
          onClick={handleClose}
          label={translate('const.cancel')}
          useSmallVersionBreakpoint={false}
          className={classes.actionsButton}
        />
      </DialogActions>
    </Dialog>
  )
}

export const AnnouncementsDialog = ({
  open,
  close,
  resource,
}: {
  open: boolean
  close: () => void
  resource: ResourceName
}) => {
  const classes = useStyles()
  const [eventsListAnnouncementValue, setEventsListAnnouncementValue] =
    useState('')
  const [variantsListAnnouncementValue, setVariantsListAnnouncementValue] =
    useState('')
  const [cartAnnouncementValue, setCartAnnouncementValue] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const { record: ticketMachine } =
    useShowContext<IksorisParkingTicketCashMachineDto>()
  const [mutate] = useMutation()
  const refresh = useRefresh()
  const translate = useTranslate()
  const notify = useNotify()

  useEffect(() => {
    if (open && ticketMachine && ticketMachine?.id) {
      mutate(
        {
          type: sanitizeFetchType('getAnnouncements'),
          resource: ResourceName.SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES,
          payload: { ticketMachineId: ticketMachine.id },
        },
        {
          returnPromise: true,
          onSuccess: (data) => {
            setEventsListAnnouncementValue(
              data?.data?.announcements?.eventsListText
                ? data.data.announcements.eventsListText
                : '',
            )
            setVariantsListAnnouncementValue(
              data?.data?.announcements?.variantsListText
                ? data.data.announcements.variantsListText
                : '',
            )
            setCartAnnouncementValue(
              data?.data?.announcements?.cartText
                ? data.data.announcements.cartText
                : '',
            )
          },
        },
      )
    }
  }, [open, ticketMachine, mutate])

  const handleClose = () => {
    setEventsListAnnouncementValue('')
    setVariantsListAnnouncementValue('')
    setCartAnnouncementValue('')
    close()
  }

  const handleSetAnnouncements = async () => {
    setConfirmOpen(false)
    await mutate(
      {
        type: sanitizeFetchType('setAnnouncements'),
        resource,
        payload: {
          ticketMachineId: ticketMachine?.id,
          announcements: {
            eventsListText: eventsListAnnouncementValue,
            variantsListText: variantsListAnnouncementValue,
            cartText: cartAnnouncementValue,
          },
        },
      },
      {
        returnPromise: true,
        onFailure: (err) => notify(err?.message, 'error'),
      },
    )
    refresh()
    handleClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableBackdropClick
      disableEscapeKeyDown
      PaperComponent={DraggableComponent}
      aria-labelledby="draggable-dialog-title"
    >
      <ConfirmDialog
        open={confirmOpen}
        cancel={() => setConfirmOpen(false)}
        confirm={handleSetAnnouncements}
        source="announcements"
        action="set"
        resource={resource}
      />
      <DialogActions
        className={classes.bulkStateActions}
        style={{ cursor: 'move' }}
      >
        <Typography
          className={classes.bulkProgressTitle}
          id="draggable-dialog-title"
        >
          {translate(
            'resources.selfpos-access-ticket-cashless-machines.dialogs.announcements.title',
          )}
        </Typography>
        <IconButton
          size="small"
          className={classes.bulkProgressCloseButton}
          onClick={handleClose}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogActions>
      <DialogContent>
        <TextField
          className={classes.textArea}
          label={translate(
            'resources.selfpos-access-ticket-cashless-machines.dialogs.announcements.eventsListText.label',
          )}
          variant="outlined"
          value={eventsListAnnouncementValue}
          onChange={(e) => setEventsListAnnouncementValue(e.target.value)}
          fullWidth
          multiline
        />
        <TextField
          className={classes.textArea}
          label={translate(
            'resources.selfpos-access-ticket-cashless-machines.dialogs.announcements.variantsListText.label',
          )}
          variant="outlined"
          value={variantsListAnnouncementValue}
          onChange={(e) => setVariantsListAnnouncementValue(e.target.value)}
          fullWidth
          multiline
        />
        <TextField
          className={classes.textArea}
          label={translate(
            'resources.selfpos-access-ticket-cashless-machines.dialogs.announcements.cartText.label',
          )}
          variant="outlined"
          value={cartAnnouncementValue}
          onChange={(e) => setCartAnnouncementValue(e.target.value)}
          fullWidth
          multiline
        />
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={() => setConfirmOpen(true)}
          label={translate('ra.action.save')}
          useSmallVersionBreakpoint={false}
          className={classes.actionsButton}
          startIcon={<Save />}
        />
        <Button
          variant="contained"
          onClick={handleClose}
          label={translate('const.cancel')}
          useSmallVersionBreakpoint={false}
          className={classes.actionsButton}
        />
      </DialogActions>
    </Dialog>
  )
}

export const ServiceCashPaymentDialog = ({
  open,
  close,
  resource,
}: {
  open: boolean
  close: () => void
  resource: ResourceName
}) => {
  const classes = useStyles()
  const [amountValue, setAmountValue] = useState('')
  const [amountError, setAmountError, amountErrorCurrentValue] =
    useStateRef(false)
  const [amountErrorText, setAmountErrorText] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const { record: ticketMachine } =
    useShowContext<IksorisParkingTicketCashMachineDto>()
  const [mutate] = useMutation()
  const refresh = useRefresh()
  const translate = useTranslate()
  const notify = useNotify()

  const handleClose = () => {
    setAmountValue('')
    setAmountError(false)
    setAmountErrorText('')
    close()
  }

  const handleSetAutomatAPICredentials = async () => {
    setConfirmOpen(false)
    setAmountError(false)
    setAmountErrorText('')

    if (!amountErrorCurrentValue.current) {
      await mutate(
        {
          type: sanitizeFetchType('serviceCashPayment'),
          resource,
          payload: {
            ticketMachineId: ticketMachine?.id,
            amount: amountValue,
          },
        },
        {
          returnPromise: true,
          onFailure: (err) => notify(err?.message, 'error'),
        },
      )
      refresh()
      handleClose()
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableBackdropClick
      disableEscapeKeyDown
      PaperComponent={DraggableComponent}
      aria-labelledby="draggable-dialog-title"
    >
      <ConfirmDialog
        open={confirmOpen}
        cancel={() => setConfirmOpen(false)}
        confirm={handleSetAutomatAPICredentials}
        source="serviceCashPayment"
        action="set"
        resource={resource}
      />
      <DialogActions
        className={classes.bulkStateActions}
        style={{ cursor: 'move' }}
      >
        <Typography
          className={classes.bulkProgressTitle}
          id="draggable-dialog-title"
        >
          {translate(
            'resources.iksoris-parking-ticket-cash-machines.dialogs.serviceCashPayment.title',
          )}
        </Typography>
        <IconButton
          size="small"
          className={classes.bulkProgressCloseButton}
          onClick={handleClose}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogActions>
      <DialogContent>
        <TextField
          className={classes.dialogProperty}
          label={translate(
            'resources.iksoris-parking-ticket-cash-machines.dialogs.serviceCashPayment.amount.label',
          )}
          variant="outlined"
          value={amountValue}
          onChange={(e) => setAmountValue(e.target.value)}
          fullWidth
          error={amountError}
          helperText={amountErrorText}
        />
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={() => setConfirmOpen(true)}
          label={translate('ra.action.save')}
          useSmallVersionBreakpoint={false}
          className={classes.actionsButton}
        />
        <Button
          variant="contained"
          onClick={handleClose}
          label={translate('const.cancel')}
          useSmallVersionBreakpoint={false}
          className={classes.actionsButton}
        />
      </DialogActions>
    </Dialog>
  )
}

interface MoneyState {
  0.1: number | undefined
  0.2: number | undefined
  0.5: number | undefined
  1: number | undefined
  2: number | undefined
  5: number | undefined
  10: number | undefined
  20: number | undefined
  50: number | undefined
  100: number | undefined
  200: number | undefined
  /*#500note*/
  // 500: number | undefined
}

export const ServiceCashFloatDialog = ({
  open,
  close,
  resource,
}: {
  open: boolean
  close: () => void
  resource: ResourceName
}) => {
  const classes = useStyles()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const { record: ticketMachine } =
    useShowContext<IksorisParkingTicketCashMachineDto>()
  const [mutate] = useMutation()
  const translate = useTranslate()
  const notify = useNotify()

  const {
    data: currentMoneyState,
    loaded,
    loading,
  } = useQuery(
    {
      type: 'getMoneyState',
      resource,
      payload: {
        id: ticketMachine?.id,
      },
    },
    { enabled: open },
  )
  const [moneyState, setMoneyState] = useReducer(
    (state: MoneyState, newState: Partial<MoneyState>) => ({
      ...state,
      ...newState,
    }),
    {
      0.1: 0,
      0.2: 0,
      0.5: 0,
      1: 0,
      2: 0,
      5: 0,
      10: 0,
      20: 0,
      50: 0,
      100: 0,
      200: 0,
      /*#500note*/
      // 500: 0,
    },
  )

  useEffect(() => {
    if (loaded && currentMoneyState?.isSuccessfull)
      setMoneyState({
        ...currentMoneyState.coinMachinePayoutMoneyState,
        ...currentMoneyState.noteMachinePayoutMoneyState,
      } as MoneyState)
  }, [loaded, currentMoneyState, setMoneyState])

  const handleClose = () => {
    close()
  }

  const handleMoneyFloat = async () => {
    setConfirmOpen(false)
    await mutate(
      {
        type: sanitizeFetchType('serviceCashFloat'),
        resource,
        payload: {
          ticketMachineId: ticketMachine?.id,
          floatMoneyState: moneyState,
        },
      },
      {
        returnPromise: true,
        onFailure: (err) => notify(err?.message, 'error'),
      },
    )
    handleClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableBackdropClick
      disableEscapeKeyDown
      PaperComponent={DraggableComponent}
      aria-labelledby="draggable-dialog-title"
    >
      <ConfirmDialog
        open={confirmOpen}
        cancel={() => setConfirmOpen(false)}
        confirm={handleMoneyFloat}
        source="serviceCashFloat"
        action="set"
        resource={resource}
      />
      <DialogActions
        className={classes.bulkStateActions}
        style={{ cursor: 'move' }}
      >
        <Typography
          className={classes.bulkProgressTitle}
          id="draggable-dialog-title"
        >
          {translate(
            'resources.iksoris-parking-ticket-cash-machines.dialogs.serviceCashFloat.title',
          )}
        </Typography>
        <IconButton
          size="small"
          className={classes.bulkProgressCloseButton}
          onClick={handleClose}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogActions>
      {currentMoneyState?.isSuccessfull ? (
        <DialogContent>
          <div>
            <div style={{ width: '-webkit-fill-available' }}>
              <TextField
                variant="filled"
                className={classes.dialogProperty}
                label="0.1 zł"
                type="number"
                inputProps={{
                  min: 0,
                  max: currentMoneyState.coinMachinePayoutMoneyState[0.1],
                }}
                value={moneyState[0.1]}
                onChange={(e) => {
                  setMoneyState({
                    0.1: e.target.value ? Number(e.target.value) : undefined,
                  })
                }}
                fullWidth
                disabled={
                  currentMoneyState.coinMachinePayoutMoneyState[0.1] === 0
                }
                error={
                  moneyState[0.1] === undefined ||
                  Number(moneyState[0.1]) < 0 ||
                  Number(moneyState[0.1]) >
                    currentMoneyState.coinMachinePayoutMoneyState[0.1]
                }
              />
              <TextField
                variant="filled"
                className={classes.dialogProperty}
                label="0.2 zł"
                type="number"
                inputProps={{
                  min: 0,
                  max: currentMoneyState.coinMachinePayoutMoneyState[0.2],
                }}
                value={moneyState[0.2]}
                onChange={(e) => {
                  setMoneyState({
                    0.2: e.target.value ? Number(e.target.value) : undefined,
                  })
                }}
                fullWidth
                disabled={
                  currentMoneyState.coinMachinePayoutMoneyState[0.2] === 0
                }
                error={
                  moneyState[0.2] === undefined ||
                  Number(moneyState[0.2]) < 0 ||
                  Number(moneyState[0.2]) >
                    currentMoneyState.coinMachinePayoutMoneyState[0.2]
                }
              />
              <TextField
                variant="filled"
                className={classes.dialogProperty}
                label="0.5 zł"
                type="number"
                inputProps={{
                  min: 0,
                  max: currentMoneyState.coinMachinePayoutMoneyState[0.5],
                }}
                value={moneyState[0.5]}
                onChange={(e) => {
                  setMoneyState({
                    0.5: e.target.value ? Number(e.target.value) : undefined,
                  })
                }}
                fullWidth
                disabled={
                  currentMoneyState.coinMachinePayoutMoneyState[0.5] === 0
                }
                error={
                  moneyState[0.5] === undefined ||
                  Number(moneyState[0.5]) < 0 ||
                  Number(moneyState[0.5]) >
                    currentMoneyState.coinMachinePayoutMoneyState[0.5]
                }
              />
              <TextField
                variant="filled"
                className={classes.dialogProperty}
                label="1 zł"
                type="number"
                inputProps={{
                  min: 0,
                  max: currentMoneyState.coinMachinePayoutMoneyState[1],
                }}
                value={moneyState[1]}
                onChange={(e) => {
                  setMoneyState({
                    1: e.target.value ? Number(e.target.value) : undefined,
                  })
                }}
                fullWidth
                disabled={
                  currentMoneyState.coinMachinePayoutMoneyState[1] === 0
                }
                error={
                  moneyState[1] === undefined ||
                  Number(moneyState[1]) < 0 ||
                  Number(moneyState[1]) >
                    currentMoneyState.coinMachinePayoutMoneyState[1]
                }
              />
              <TextField
                variant="filled"
                className={classes.dialogProperty}
                label="2 zł"
                type="number"
                inputProps={{
                  min: 0,
                  max: currentMoneyState.coinMachinePayoutMoneyState[2],
                }}
                value={moneyState[2]}
                onChange={(e) => {
                  setMoneyState({
                    2: e.target.value ? Number(e.target.value) : undefined,
                  })
                }}
                fullWidth
                disabled={
                  currentMoneyState.coinMachinePayoutMoneyState[2] === 0
                }
                error={
                  moneyState[2] === undefined ||
                  Number(moneyState[2]) < 0 ||
                  Number(moneyState[2]) >
                    currentMoneyState.coinMachinePayoutMoneyState[2]
                }
              />
              <TextField
                variant="filled"
                className={classes.dialogProperty}
                label="5 zł"
                type="number"
                inputProps={{
                  min: 0,
                  max: currentMoneyState.coinMachinePayoutMoneyState[5],
                }}
                value={moneyState[5]}
                onChange={(e) => {
                  setMoneyState({
                    5: e.target.value ? Number(e.target.value) : undefined,
                  })
                }}
                fullWidth
                disabled={
                  currentMoneyState.coinMachinePayoutMoneyState[5] === 0
                }
                error={
                  moneyState[5] === undefined ||
                  Number(moneyState[5]) < 0 ||
                  Number(moneyState[5]) >
                    currentMoneyState.coinMachinePayoutMoneyState[5]
                }
              />
            </div>
            <div style={{ width: '2rem' }} />
            <div style={{ width: '-webkit-fill-available' }}>
              <TextField
                variant="filled"
                className={classes.dialogProperty}
                label="10 zł"
                type="number"
                inputProps={{
                  min: 0,
                  max: currentMoneyState.noteMachinePayoutMoneyState[10],
                }}
                value={moneyState[10]}
                onChange={(e) => {
                  setMoneyState({
                    10: e.target.value ? Number(e.target.value) : undefined,
                  })
                }}
                fullWidth
                disabled={
                  currentMoneyState.noteMachinePayoutMoneyState[10] === 0
                }
                error={
                  moneyState[10] === undefined ||
                  Number(moneyState[10]) < 0 ||
                  Number(moneyState[10]) >
                    currentMoneyState.noteMachinePayoutMoneyState[10]
                }
              />
              <TextField
                variant="filled"
                className={classes.dialogProperty}
                label="20 zł"
                type="number"
                inputProps={{
                  min: 0,
                  max: currentMoneyState.noteMachinePayoutMoneyState[20],
                }}
                value={moneyState[20]}
                onChange={(e) => {
                  setMoneyState({
                    20: e.target.value ? Number(e.target.value) : undefined,
                  })
                }}
                fullWidth
                disabled={
                  currentMoneyState.noteMachinePayoutMoneyState[20] === 0
                }
                error={
                  moneyState[20] === undefined ||
                  Number(moneyState[20]) < 0 ||
                  Number(moneyState[20]) >
                    currentMoneyState.noteMachinePayoutMoneyState[20]
                }
              />
              <TextField
                variant="filled"
                className={classes.dialogProperty}
                label="50 zł"
                type="number"
                inputProps={{
                  min: 0,
                  max: currentMoneyState.noteMachinePayoutMoneyState[50],
                }}
                value={moneyState[50]}
                onChange={(e) => {
                  setMoneyState({
                    50: e.target.value ? Number(e.target.value) : undefined,
                  })
                }}
                fullWidth
                disabled={
                  currentMoneyState.noteMachinePayoutMoneyState[50] === 0
                }
                error={
                  moneyState[50] === undefined ||
                  Number(moneyState[50]) < 0 ||
                  Number(moneyState[50]) >
                    currentMoneyState.noteMachinePayoutMoneyState[50]
                }
              />
              <TextField
                variant="filled"
                className={classes.dialogProperty}
                label="100 zł"
                type="number"
                inputProps={{
                  min: 0,
                  max: currentMoneyState.noteMachinePayoutMoneyState[100],
                }}
                value={moneyState[100]}
                onChange={(e) => {
                  setMoneyState({
                    100: e.target.value ? Number(e.target.value) : undefined,
                  })
                }}
                fullWidth
                disabled={
                  currentMoneyState.noteMachinePayoutMoneyState[100] === 0
                }
                error={
                  moneyState[100] === undefined ||
                  Number(moneyState[100]) < 0 ||
                  Number(moneyState[100]) >
                    currentMoneyState.noteMachinePayoutMoneyState[100]
                }
              />
              <TextField
                variant="filled"
                className={classes.dialogProperty}
                label="200 zł"
                type="number"
                inputProps={{
                  min: 0,
                  max: currentMoneyState.noteMachinePayoutMoneyState[200],
                }}
                value={moneyState[200]}
                onChange={(e) => {
                  setMoneyState({
                    200: e.target.value ? Number(e.target.value) : undefined,
                  })
                }}
                fullWidth
                disabled={
                  currentMoneyState.noteMachinePayoutMoneyState[200] === 0
                }
                error={
                  moneyState[200] === undefined ||
                  Number(moneyState[200]) < 0 ||
                  Number(moneyState[200]) >
                    currentMoneyState.noteMachinePayoutMoneyState[200]
                }
              />
              {/*#500note*/}
              {/* <TextField
                variant="filled"
                className={classes.dialogProperty}
                label="500 zł"
                type="number"
                inputProps={{
                  min: 0,
                  max: currentMoneyState.noteMachinePayoutMoneyState[500],
                }}
                value={moneyState[500]}
                onChange={(e) => {
                  setMoneyState({
                    500: e.target.value ? Number(e.target.value) : undefined,
                  })
                }}
                fullWidth
                disabled={
                  currentMoneyState.noteMachinePayoutMoneyState[500] === 0
                }
                error={
                  moneyState[500] === undefined ||
                  Number(moneyState[500]) < 0 ||
                  Number(moneyState[500]) >
                    currentMoneyState.noteMachinePayoutMoneyState[500]
                }
              /> */}
            </div>
          </div>
        </DialogContent>
      ) : (
        <DialogContent>
          {!loading && open && (
            <Typography
              variant="h4"
              paragraph
              style={{ marginTop: 16, marginBottom: 16, textAlign: 'center' }}
            >
              {translate(
                'resources.automat-api-transactions.dialogs.getMoneyState.error',
              )}
            </Typography>
          )}
        </DialogContent>
      )}
      {currentMoneyState?.isSuccessfull && (
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => setConfirmOpen(true)}
            label={translate('ra.action.save')}
            useSmallVersionBreakpoint={false}
            className={classes.actionsButton}
          />
          <Button
            variant="contained"
            onClick={handleClose}
            label={translate('const.cancel')}
            useSmallVersionBreakpoint={false}
            className={classes.actionsButton}
          />
        </DialogActions>
      )}
    </Dialog>
  )
}

export const PrintTransactionConfirmationDialog = ({
  open,
  close,
  resource,
}: {
  open: boolean
  close: () => void
  resource: ResourceName
}) => {
  const classes = useStyles()
  const { record: ticketMachine } =
    useShowContext<IksorisParkingTicketCashMachineDto>()
  const translate = useTranslate()
  const [openPrintDialog, setOpenPrintDialog] = useState<boolean>(false)
  const [selectedRecord, setSelectedRecord] =
    useState<TransactionInfoResponse | null>(null)
  const [printing, setPrinting] = useState<boolean>(false)
  const theme = useTheme()

  const handleCloseList = () => {
    close()
  }

  const handleClosePrintDialog = () => {
    setOpenPrintDialog(false)
    setSelectedRecord(null)
  }

  const ListFilters = (props) => (
    <Filter {...props}>
      <SelectInput
        alwaysOn
        source="transactionStatus"
        label="resources.automat-api-transactions.fields.transactionInfo.transactionStatus"
        choices={Object.entries(TransactionStatus).map(([value]) => ({
          id: value,
          name: `resources.enums.TransactionStatus.${value}`,
        }))}
      />
      <SelectInput
        alwaysOn
        source="transactionType"
        label="resources.automat-api-transactions.fields.transactionInfo.transactionType"
        choices={Object.entries(TransactionType).map(([value]) => ({
          id: value,
          name: `resources.enums.TransactionType.${value}`,
        }))}
      />
    </Filter>
  )

  const PrintTransactionForm = () => {
    setPrinting(true)
  }

  const forceLandscape = () => {
    const style = document.createElement('style')
    style.innerHTML = `@page {size: landscape}`
    style.id = 'page-orientation'
    document.head.appendChild(style)
  }

  useEffect(() => {
    if (printing) {
      forceLandscape()
      window.print()
      setPrinting(false)
    }
  }, [printing])

  return (
    <>
      <ThemeProvider theme={printing ? extendedLightTheme : theme}>
        <Dialog
          open={openPrintDialog}
          onClose={() => setOpenPrintDialog(false)}
          maxWidth="xl"
        >
          <DialogActions
            className={classes.bulkStateActions}
            style={{
              cursor: 'move',
              contentVisibility: printing ? 'hidden' : 'visible',
            }}
          >
            <IconButton
              size="small"
              className={classes.bulkProgressCloseButton}
              onClick={handleClosePrintDialog}
            >
              <Close fontSize="small" />
            </IconButton>
          </DialogActions>
          <DialogContent>
            {selectedRecord && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 32,
                  paddingLeft: '15px',
                }}
              >
                <div>
                  <Typography variant="h5" style={{ paddingLeft: '20px' }}>
                    {translate(
                      'resources.automat-api-transactions.fields.transactionInfo.transactionSummary',
                    )}
                  </Typography>
                  <div style={{ display: 'flex', gap: 32 }}>
                    <div style={{ flex: 1, minWidth: 290 }}>
                      {/* Column 1 */}
                      <SimpleShowLayout
                        record={selectedRecord}
                        resource={resource}
                      >
                        <RATextField source="transactionInfo.id" />
                        <RATextField
                          source="transactionInfo.externalTransactionID"
                          emptyText="-"
                        />
                        <FunctionField<TransactionInfoResponse>
                          source="transactionInfo.transactionStatus"
                          render={(record?: TransactionInfoResponse) => (
                            <div>
                              {translate(
                                `resources.enums.TransactionStatus.${record?.transactionInfo.transactionStatus}`,
                              )}
                            </div>
                          )}
                        />
                        <FunctionField<TransactionInfoResponse>
                          source="transactionInfo.transactionType"
                          render={(record?: TransactionInfoResponse) => (
                            <div>
                              {translate(
                                `resources.enums.TransactionType.${record?.transactionInfo.transactionType}`,
                              )}
                            </div>
                          )}
                        />
                        <FunctionField<TransactionInfoResponse>
                          source="transactionInfo.cancelledAfterStart"
                          render={(record?: TransactionInfoResponse) => (
                            <div>
                              {record?.transactionInfo.cancelledAfterStart
                                ? translate('const.yes')
                                : translate('const.no')}
                            </div>
                          )}
                        />
                        {(selectedRecord.transactionInfo
                          .transactionCancellationReason && (
                          <FunctionField<TransactionInfoResponse>
                            source="transactionInfo.transactionCancellationReason"
                            render={(record?: TransactionInfoResponse) => (
                              <div>
                                {translate(
                                  `resources.enums.TransactionCancellationReasons.${record?.transactionInfo.transactionCancellationReason}`,
                                )}
                              </div>
                            )}
                          />
                        )) || (
                          <RATextField
                            source="transactionInfo.transactionCancellationReason"
                            emptyText="-"
                          />
                        )}
                      </SimpleShowLayout>
                    </div>
                    <div style={{ flex: 1, minWidth: 250 }}>
                      {/* Column 2 */}
                      <SimpleShowLayout
                        record={selectedRecord}
                        resource={resource}
                      >
                        <RATextField
                          source="transactionInfo.paymentTitle"
                          emptyText="-"
                        />
                        <FunctionField<TransactionInfoResponse>
                          source="transactionInfo.paymentType"
                          render={(record?: TransactionInfoResponse) => (
                            <div>
                              {translate(
                                `resources.enums.PaymentType.${record?.transactionInfo.paymentType}`,
                              )}
                            </div>
                          )}
                        />
                        <RATextField
                          source="transactionInfo.annotation"
                          emptyText="-"
                        />
                        <FunctionField<TransactionInfoResponse>
                          source="transactionInfo.servicePaymentFinishedPartially"
                          render={(record?: TransactionInfoResponse) => (
                            <div>
                              {record?.transactionInfo
                                .servicePaymentFinishedPartially
                                ? translate('const.yes')
                                : translate('const.no')}
                            </div>
                          )}
                        />
                        <Labeled source="transactionInfo.amountRequested">
                          <Typography>
                            {selectedRecord.transactionInfo.amountRequested} zł
                          </Typography>
                        </Labeled>
                        <Labeled source="transactionInfo.amountProcessed">
                          <Typography>
                            {selectedRecord.transactionInfo.amountProcessed} zł
                          </Typography>
                        </Labeled>
                        {(selectedRecord.transactionInfo
                          .finishedTransactionErrorCode && (
                          <FunctionField<TransactionInfoResponse>
                            source="transactionInfo.finishedTransactionErrorCode"
                            render={(record?: TransactionInfoResponse) => (
                              <div>
                                {translate(
                                  `resources.enums.TransactionFinishedErrorCodes.${record?.transactionInfo.finishedTransactionErrorCode}`,
                                )}
                              </div>
                            )}
                          />
                        )) || (
                          <RATextField
                            source="transactionInfo.finishedTransactionErrorCode"
                            emptyText="-"
                          />
                        )}
                      </SimpleShowLayout>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0,
                      }}
                    >
                      {/* Coin Machine Info */}
                      <div>
                        <Typography variant="subtitle1">
                          {translate(
                            'resources.automat-api-transactions.transactionInfo.coinMachineOverallState',
                          )}
                        </Typography>
                        <div style={{ display: 'flex', gap: 24 }}>
                          {/* Coin Column 1 */}
                          <div style={{ flex: 1, minWidth: 180 }}>
                            <SimpleShowLayout
                              record={selectedRecord}
                              resource={resource}
                            >
                              <Labeled source="transactionInfo.moneyLog.amountInCashbox">
                                <Typography>
                                  {selectedRecord?.transactionInfo
                                    ?.coinMachineStartingMoneyLog
                                    ?.amountInCashbox || 0}{' '}
                                  zł /{' '}
                                  {selectedRecord?.transactionInfo
                                    ?.coinMachineEndingMoneyLog
                                    ?.amountInCashbox || 0}{' '}
                                  zł
                                </Typography>
                              </Labeled>
                              <Labeled source="transactionInfo.moneyLog.amountInPayout">
                                <Typography>
                                  {selectedRecord?.transactionInfo
                                    ?.coinMachineStartingMoneyLog
                                    ?.amountInPayout || 0}{' '}
                                  zł /{' '}
                                  {selectedRecord?.transactionInfo
                                    ?.coinMachineEndingMoneyLog
                                    ?.amountInPayout || 0}{' '}
                                  zł
                                </Typography>
                              </Labeled>
                              <FunctionField<TransactionInfoResponse>
                                source="transactionInfo.coinMachineStartingMoneyLog.cashboxRemoved"
                                label="resources.automat-api-transactions.fields.transactionInfo.moneyLog.cashboxRemoved"
                                render={(record?: TransactionInfoResponse) => (
                                  <div>
                                    {translate(
                                      record?.transactionInfo
                                        .coinMachineStartingMoneyLog
                                        ?.cashboxRemoved
                                        ? 'const.yes'
                                        : 'const.no',
                                    )}
                                    {' / '}
                                    {translate(
                                      record?.transactionInfo
                                        .coinMachineEndingMoneyLog
                                        ?.cashboxRemoved
                                        ? 'const.yes'
                                        : 'const.no',
                                    )}
                                  </div>
                                )}
                              />
                            </SimpleShowLayout>
                          </div>
                          {/* Coin Column 2 */}
                          <div style={{ flex: 1, minWidth: 180 }}>
                            <SimpleShowLayout
                              record={selectedRecord}
                              resource={resource}
                            >
                              <FunctionField<TransactionInfoResponse>
                                source="transactionInfo.coinMachineStartingMoneyLog.moneyState"
                                label="resources.automat-api-transactions.fields.transactionInfo.moneyLog.moneyState"
                                render={(record?: TransactionInfoResponse) =>
                                  (
                                    <>
                                      {['0.1', '0.2', '0.5', '1', '2', '5'].map(
                                        (denomination) => (
                                          <Typography key={denomination}>
                                            {denomination} zł -{' '}
                                            {record?.transactionInfo
                                              ?.coinMachineStartingMoneyLog
                                              ?.moneyState?.[denomination] ||
                                              '0'}
                                            {' / '}
                                            {record?.transactionInfo
                                              ?.coinMachineEndingMoneyLog
                                              ?.moneyState?.[denomination] ||
                                              '0'}{' '}
                                            {translate(
                                              'resources.automat-api-transactions.dialogs.getMoneyState.pcs',
                                            )}
                                          </Typography>
                                        ),
                                      )}
                                    </>
                                  ) || (
                                    <RATextField
                                      source="transactionInfo.moneyLog.moneyState"
                                      emptyText="-"
                                    />
                                  )
                                }
                              />
                            </SimpleShowLayout>
                          </div>
                          {/* Coin Column 3 */}
                          <div style={{ flex: 1, minWidth: 180 }}>
                            <SimpleShowLayout
                              record={selectedRecord}
                              resource={resource}
                            >
                              <Labeled source="transactionInfo.moneyLog.id">
                                <Typography>
                                  {
                                    selectedRecord?.transactionInfo
                                      ?.coinMachineStartingMoneyLog?.id
                                  }
                                  {' / '}
                                  {
                                    selectedRecord?.transactionInfo
                                      ?.coinMachineEndingMoneyLog?.id
                                  }
                                </Typography>
                              </Labeled>
                              <RATextField
                                source="transactionInfo.coinMachineStartingMoneyLog.deviceName"
                                label="resources.automat-api-transactions.fields.transactionInfo.moneyLog.deviceName"
                              />
                              {(selectedRecord.transactionInfo
                                .coinMachineStartingMoneyLog?.deviceType && (
                                <FunctionField<TransactionInfoResponse>
                                  source="transactionInfo.coinMachineStartingMoneyLog.deviceType"
                                  label="resources.automat-api-transactions.fields.transactionInfo.moneyLog.deviceType"
                                  render={(
                                    record?: TransactionInfoResponse,
                                  ) => (
                                    <div>
                                      {translate(
                                        `resources.enums.MoneyDeviceType.${record?.transactionInfo.coinMachineStartingMoneyLog?.deviceType}`,
                                      )}
                                    </div>
                                  )}
                                />
                              )) || (
                                <RATextField
                                  source="transactionInfo.coinMachineStartingMoneyLog.deviceType"
                                  emptyText="-"
                                />
                              )}
                            </SimpleShowLayout>
                          </div>
                        </div>
                      </div>

                      {/* Note Machine Info */}
                      <div>
                        <Typography variant="subtitle1">
                          {translate(
                            'resources.automat-api-transactions.transactionInfo.noteMachineOverallState',
                          )}
                        </Typography>
                        <div style={{ display: 'flex', gap: 24 }}>
                          {/* Note Column 1 */}
                          <div style={{ flex: 1, minWidth: 180 }}>
                            <SimpleShowLayout
                              record={selectedRecord}
                              resource={resource}
                            >
                              <Labeled source="transactionInfo.moneyLog.amountInCashbox">
                                <Typography>
                                  {selectedRecord?.transactionInfo
                                    ?.noteMachineStartingMoneyLog
                                    ?.amountInCashbox || 0}{' '}
                                  zł /{' '}
                                  {selectedRecord?.transactionInfo
                                    ?.noteMachineEndingMoneyLog
                                    ?.amountInCashbox || 0}{' '}
                                  zł
                                </Typography>
                              </Labeled>
                              <Labeled source="transactionInfo.moneyLog.amountInPayout">
                                <Typography>
                                  {selectedRecord?.transactionInfo
                                    ?.noteMachineStartingMoneyLog
                                    ?.amountInPayout || 0}{' '}
                                  zł /{' '}
                                  {selectedRecord?.transactionInfo
                                    ?.noteMachineEndingMoneyLog
                                    ?.amountInPayout || 0}{' '}
                                  zł
                                </Typography>
                              </Labeled>
                              <FunctionField<TransactionInfoResponse>
                                source="transactionInfo.noteMachineStartingMoneyLog.cashboxRemoved"
                                label="resources.automat-api-transactions.fields.transactionInfo.moneyLog.cashboxRemoved"
                                render={(record?: TransactionInfoResponse) => (
                                  <div>
                                    {translate(
                                      record?.transactionInfo
                                        .noteMachineStartingMoneyLog
                                        ?.cashboxRemoved
                                        ? 'const.yes'
                                        : 'const.no',
                                    )}
                                    {' / '}
                                    {translate(
                                      record?.transactionInfo
                                        .noteMachineEndingMoneyLog
                                        ?.cashboxRemoved
                                        ? 'const.yes'
                                        : 'const.no',
                                    )}
                                  </div>
                                )}
                              />
                            </SimpleShowLayout>
                          </div>
                          {/* Note Column 2 */}
                          <div style={{ flex: 1, minWidth: 180 }}>
                            <SimpleShowLayout
                              record={selectedRecord}
                              resource={resource}
                            >
                              {(selectedRecord.transactionInfo
                                .noteMachineStartingMoneyLog?.moneyState && (
                                <FunctionField<TransactionInfoResponse>
                                  source="transactionInfo.noteMachineStartingMoneyLog.moneyState"
                                  label="resources.automat-api-transactions.fields.transactionInfo.moneyLog.moneyState"
                                  render={(
                                    record?: TransactionInfoResponse,
                                  ) => (
                                    <>
                                      {[
                                        '10',
                                        '20',
                                        '50',
                                        '100',
                                        '200',
                                        /*#500note*/
                                        // '500',
                                      ].map((denomination) => (
                                        <Typography key={denomination}>
                                          {denomination} zł -{' '}
                                          {record?.transactionInfo
                                            ?.noteMachineStartingMoneyLog
                                            ?.moneyState?.[denomination] || '0'}
                                          {' / '}
                                          {record?.transactionInfo
                                            ?.noteMachineEndingMoneyLog
                                            ?.moneyState?.[denomination] ||
                                            '0'}{' '}
                                          {translate(
                                            'resources.automat-api-transactions.dialogs.getMoneyState.pcs',
                                          )}
                                        </Typography>
                                      ))}
                                    </>
                                  )}
                                />
                              )) || (
                                <RATextField
                                  source="transactionInfo.moneyLog.moneyState"
                                  emptyText="-"
                                />
                              )}
                            </SimpleShowLayout>
                          </div>
                          {/* Note Column 3 */}
                          <div style={{ flex: 1, minWidth: 180 }}>
                            <SimpleShowLayout
                              record={selectedRecord}
                              resource={resource}
                            >
                              <Labeled source="transactionInfo.moneyLog.id">
                                <Typography>
                                  {
                                    selectedRecord?.transactionInfo
                                      ?.noteMachineStartingMoneyLog?.id
                                  }
                                  {' / '}
                                  {
                                    selectedRecord?.transactionInfo
                                      ?.noteMachineEndingMoneyLog?.id
                                  }
                                </Typography>
                              </Labeled>
                              <RATextField
                                source="transactionInfo.noteMachineStartingMoneyLog.deviceName"
                                label="resources.automat-api-transactions.fields.transactionInfo.moneyLog.deviceName"
                              />
                              {(selectedRecord.transactionInfo
                                .noteMachineStartingMoneyLog?.deviceType && (
                                <FunctionField<TransactionInfoResponse>
                                  source="transactionInfo.noteMachineStartingMoneyLog.deviceType"
                                  label="resources.automat-api-transactions.fields.transactionInfo.moneyLog.deviceType"
                                  render={(
                                    record?: TransactionInfoResponse,
                                  ) => (
                                    <div>
                                      {translate(
                                        `resources.enums.MoneyDeviceType.${record?.transactionInfo.noteMachineStartingMoneyLog?.deviceType}`,
                                      )}
                                    </div>
                                  )}
                                />
                              )) || (
                                <RATextField
                                  source={`resources.enums.MoneyDeviceType.${selectedRecord.transactionInfo.noteMachineStartingMoneyLog?.deviceType}`}
                                  emptyText="-"
                                />
                              )}
                            </SimpleShowLayout>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
          <DialogActions
            style={{ contentVisibility: printing ? 'hidden' : 'visible' }}
          >
            <Button
              variant="contained"
              onClick={PrintTransactionForm}
              label={translate(
                'resources.automat-api-transactions.dialogs.printTransactionConfirmation.printButtonLabel',
              )}
              useSmallVersionBreakpoint={false}
              className={classes.actionsButton}
            />
          </DialogActions>
        </Dialog>
      </ThemeProvider>

      <Dialog
        open={open}
        onClose={handleCloseList}
        disableBackdropClick
        disableEscapeKeyDown
        PaperComponent={DraggableComponent}
        aria-labelledby="draggable-dialog-title"
        maxWidth="xl"
        fullWidth
        style={{ contentVisibility: printing ? 'hidden' : 'visible' }}
      >
        <DialogActions
          className={classes.bulkStateActions}
          style={{ cursor: 'move' }}
        >
          <Typography
            className={classes.bulkProgressTitle}
            id="draggable-dialog-title"
          >
            {translate(
              'resources.automat-api-transactions.dialogs.printTransactionConfirmation.title',
            )}
          </Typography>
          <IconButton
            size="small"
            className={classes.bulkProgressCloseButton}
            onClick={handleCloseList}
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogActions>
        <DialogContent>
          <ResourceContextProvider value={resource}>
            <List
              exporter={false}
              filters={<ListFilters />}
              pagination={<Pagination rowsPerPageOptions={[10, 20, 50, 100]} />}
              title=" "
              basePath={`/${resource}`}
              filterDefaultValues={{
                ticketMachineId: ticketMachine?.id,
                paymentType: PaymentType.Cash,
              }}
              sort={{ field: 'transactionInfo.date', order: 'DESC' }}
              bulkActionButtons={false}
            >
              <Datagrid
                size="small"
                rowClick={(id, basePath, record) => {
                  setSelectedRecord(record as TransactionInfoResponse)
                  setOpenPrintDialog(true)
                  return ''
                }}
              >
                <RATextField source="id" label="ID" />
                <RATextField
                  source="transactionInfo.externalTransactionID"
                  sortable={false}
                />
                <FunctionField
                  source="transactionInfo.transactionType"
                  sortable={false}
                  render={(record) =>
                    translate(
                      `resources.enums.TransactionType.${record?.transactionInfo?.transactionType}`,
                    )
                  }
                />
                <FunctionField
                  source="transactionInfo.transactionStatus"
                  sortable={false}
                  render={(record) =>
                    translate(
                      `resources.enums.TransactionStatus.${record?.transactionInfo?.transactionStatus}`,
                    )
                  }
                />
                <RATextField
                  source="transactionInfo.paymentTitle"
                  sortable={false}
                />
                <DateField
                  source="transactionInfo.date"
                  showTime
                  options={{
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                  }}
                />
              </Datagrid>
            </List>
          </ResourceContextProvider>
        </DialogContent>
      </Dialog>
    </>
  )
}

export const GetMoneyStateDialog = ({
  open,
  close,
  resource,
}: {
  open: boolean
  close: () => void
  resource: ResourceName
}) => {
  const classes = useStyles()
  const { record: ticketMachine } =
    useShowContext<IksorisParkingTicketCashMachineDto>()
  const translate = useTranslate()

  const handleClose = () => {
    close()
  }

  const { data: moneyState, loading } = useQuery(
    {
      type: 'getMoneyState',
      resource,
      payload: {
        id: ticketMachine?.id,
      },
    },
    { enabled: open },
  )

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableBackdropClick
      disableEscapeKeyDown
      PaperComponent={DraggableComponent}
      aria-labelledby="draggable-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogActions
        className={classes.bulkStateActions}
        style={{ cursor: 'move' }}
      >
        <Typography
          className={classes.bulkProgressTitle}
          id="draggable-dialog-title"
        >
          {translate(
            'resources.automat-api-transactions.dialogs.getMoneyState.title',
          )}
        </Typography>
        <IconButton
          size="small"
          className={classes.bulkProgressCloseButton}
          onClick={handleClose}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogActions>
      <DialogContent>
        {moneyState?.isSuccessfull ? (
          <SimpleShowLayout record={moneyState} resource="get-money-state">
            <Labeled source="amount">
              <Typography>{moneyState.amount} zł</Typography>
            </Labeled>

            <Typography
              variant="h6"
              paragraph
              style={{ marginTop: 16, marginBottom: 0 }}
            >
              {translate(
                'resources.automat-api-transactions.dialogs.getMoneyState.coinMachineSection.title',
              )}
            </Typography>
            <Labeled source="coinMachineCashbox">
              <Typography>{moneyState.coinMachineCashbox} zł</Typography>
            </Labeled>
            <Labeled source="coinMachinePayout">
              <Typography>{moneyState.coinMachinePayout} zł</Typography>
            </Labeled>
            {moneyState.noteMachinePayoutMoneyState && (
              <FunctionField
                source="coinMachinePayoutMoneyState"
                render={(record: any) => (
                  <>
                    <Typography>
                      0.1 zł - {record.coinMachinePayoutMoneyState?.['0.1']}{' '}
                      {translate(
                        'resources.automat-api-transactions.dialogs.getMoneyState.pcs',
                      )}
                    </Typography>
                    <Typography>
                      0.2 zł - {record.coinMachinePayoutMoneyState?.['0.2']}{' '}
                      {translate(
                        'resources.automat-api-transactions.dialogs.getMoneyState.pcs',
                      )}
                    </Typography>
                    <Typography>
                      0.5 zł - {record.coinMachinePayoutMoneyState?.['0.5']}{' '}
                      {translate(
                        'resources.automat-api-transactions.dialogs.getMoneyState.pcs',
                      )}
                    </Typography>
                    <Typography>
                      1 zł - {record.coinMachinePayoutMoneyState?.['1']}{' '}
                      {translate(
                        'resources.automat-api-transactions.dialogs.getMoneyState.pcs',
                      )}
                    </Typography>
                    <Typography>
                      2 zł - {record.coinMachinePayoutMoneyState?.['2']}{' '}
                      {translate(
                        'resources.automat-api-transactions.dialogs.getMoneyState.pcs',
                      )}
                    </Typography>
                    <Typography>
                      5 zł - {record.coinMachinePayoutMoneyState?.['5']}{' '}
                      {translate(
                        'resources.automat-api-transactions.dialogs.getMoneyState.pcs',
                      )}
                    </Typography>
                  </>
                )}
              />
            )}

            <Typography
              variant="h6"
              paragraph
              style={{ marginTop: 16, marginBottom: 0 }}
            >
              {translate(
                'resources.automat-api-transactions.dialogs.getMoneyState.noteMachineSection.title',
              )}
            </Typography>
            <Labeled source="noteMachineCashbox">
              <Typography>{moneyState.noteMachineCashbox} zł</Typography>
            </Labeled>
            <Labeled source="noteMachinePayout">
              <Typography>{moneyState.noteMachinePayout} zł</Typography>
            </Labeled>
            {moneyState.noteMachinePayoutMoneyState && (
              <FunctionField
                source="noteMachinePayoutMoneyState"
                render={(record: any) => (
                  <>
                    <Typography>
                      10 zł - {record.noteMachinePayoutMoneyState?.['10']}{' '}
                      {translate(
                        'resources.automat-api-transactions.dialogs.getMoneyState.pcs',
                      )}
                    </Typography>
                    <Typography>
                      20 zł - {record.noteMachinePayoutMoneyState?.['20']}{' '}
                      {translate(
                        'resources.automat-api-transactions.dialogs.getMoneyState.pcs',
                      )}
                    </Typography>
                    <Typography>
                      50 zł - {record.noteMachinePayoutMoneyState?.['50']}{' '}
                      {translate(
                        'resources.automat-api-transactions.dialogs.getMoneyState.pcs',
                      )}
                    </Typography>
                    <Typography>
                      100 zł - {record.noteMachinePayoutMoneyState?.['100']}{' '}
                      {translate(
                        'resources.automat-api-transactions.dialogs.getMoneyState.pcs',
                      )}
                    </Typography>
                    <Typography>
                      200 zł - {record.noteMachinePayoutMoneyState?.['200']}{' '}
                      {translate(
                        'resources.automat-api-transactions.dialogs.getMoneyState.pcs',
                      )}
                    </Typography>
                    {/*#500note*/}
                    {/* <Typography>
                      500 zł - {record.noteMachinePayoutMoneyState?.['500']}{' '}
                      {translate(
                        'resources.automat-api-transactions.dialogs.getMoneyState.pcs',
                      )}
                    </Typography> */}
                  </>
                )}
              />
            )}
          </SimpleShowLayout>
        ) : (
          <>
            {!loading && open && (
              <Typography
                variant="h4"
                paragraph
                style={{ marginTop: 16, marginBottom: 16, textAlign: 'center' }}
              >
                {translate(
                  'resources.automat-api-transactions.dialogs.getMoneyState.error',
                )}
              </Typography>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
